/**
 * DebugMemoryPanel -- memory growth monitor.
 *
 * Samples heap + GPU counts + scene counts at 2 Hz, computes trough-envelope
 * slopes (robust against GC noise), and reports per-command-buffer operation
 * deltas. Designed to produce a compact JSON summary you can paste back for
 * leak analysis. See ../debug/memProbe.ts for the analyzer.
 */

import { Circle, Copy, Download, RotateCcw, Square } from "lucide-react";
import { useCallback, useSyncExternalStore } from "react";
import { useStore } from "../app/store.ts";
import {
  downloadExport,
  getSnapshot,
  MEMPROBE_SCHEMA_VERSION,
  type ProbeSnapshot,
  type RateReport,
  resetProbe,
  type SlopeReport,
  serializeJSONL,
  serializeSummary,
  startProbe,
  stopProbe,
  subscribe,
} from "../debug/memProbe.ts";
import { Button } from "./Button.tsx";
import { DraggablePanel } from "./DraggablePanel.tsx";
import { usePanelSelection } from "./floatingPanelStore.ts";
import { useTransientFeedback } from "./shared.tsx";
import {
  COLOR_DEBUG_OVERLAY_BG,
  COLOR_TEXT_DIM,
  COLOR_TEXT_FAINT,
  FONT_SIZE_TINY,
  RADIUS_XS,
  Z_DEBUG,
} from "./tokens.ts";

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function fmtBytes(b: number | null | undefined): string {
  const safe = Number(b ?? 0);
  if (!Number.isFinite(safe)) return "0 B";
  const abs = Math.abs(safe);
  if (abs < 1024) return `${safe.toFixed(0)} B`;
  if (abs < 1024 * 1024) return `${(safe / 1024).toFixed(1)} KB`;
  return `${(safe / (1024 * 1024)).toFixed(2)} MB`;
}

function fmtRate(bytesPerSec: number): string {
  const perMin = bytesPerSec * 60;
  return `${fmtBytes(perMin)}/min`;
}

function fmtDuration(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

function fmtCount(n: number | null | undefined): string {
  const safe = Number(n ?? 0);
  return Math.round(Number.isFinite(safe) ? safe : 0).toLocaleString();
}

function verdict(
  slope: number,
  r2: number,
  threshold: number,
): { color: string; label: string } {
  if (Math.abs(slope) < threshold) return { color: "#0f0", label: "flat" };
  if (slope > 0 && r2 >= 0.5) return { color: "#f44", label: "LEAK" };
  if (slope > 0 && r2 >= 0.2) return { color: "#fa0", label: "rising?" };
  if (slope > 0) return { color: "#aa0", label: "noisy+" };
  return { color: "#0af", label: "falling" };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DebugMemoryPanel() {
  const debugEnabled = useStore((s) => s.debugEnabled);
  const panelOpen = useStore((s) => s.debugPanelOpen.memoryProbe);
  const togglePanel = useStore((s) => s.toggleDebugPanel);
  const sel = usePanelSelection("debug-memory", () =>
    togglePanel("memoryProbe"),
  );

  const snap = useSyncExternalStore(subscribe, getSnapshot);
  const [copied, fireCopied] = useTransientFeedback(1500);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(serializeSummary()).then(fireCopied);
  }, [fireCopied]);

  const handleDownload = useCallback(() => {
    const ts = new Date().toISOString().replace(/[:.]/g, "-");
    downloadExport(
      serializeJSONL(),
      `memprobe-v${MEMPROBE_SCHEMA_VERSION}-${ts}.jsonl`,
      "application/x-ndjson",
    );
  }, []);

  if (!debugEnabled || !panelOpen) return null;

  return (
    <DraggablePanel
      id="debug-memory"
      defaultPosition={{ bottom: 8, left: 8 }}
      zIndex={Z_DEBUG}
      collapsible
      resizable
      defaultSize={{ width: 360, height: 520 }}
      minSize={{ width: 280, height: 220 }}
      maxSize={{ width: 560, height: 760 }}
      selectionColor="#ff0"
      tint="rgba(0,255,0,0.06)"
      style={{ background: COLOR_DEBUG_OVERLAY_BG }}
      {...sel}
      header={<MemoryHeader snap={snap} />}
    >
      <div
        style={{
          background: COLOR_DEBUG_OVERLAY_BG,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          borderRadius: "0 0 6px 6px",
          overflow: "hidden",
          color: "#0f0",
          fontFamily: "monospace",
          fontSize: FONT_SIZE_TINY,
        }}
      >
        <Toolbar
          snap={snap}
          onCopy={handleCopy}
          onDownload={handleDownload}
          copied={copied}
        />
        <div style={{ flex: 1, overflowY: "auto", padding: "6px 8px" }}>
          <SlopesSection snap={snap} />
          <PressureSection snap={snap} />
          <EditPreviewSection snap={snap} />
          <RetainedStateSection snap={snap} />
          <OpsSection snap={snap} />
          <SpikesSection snap={snap} />
        </div>
      </div>
    </DraggablePanel>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function MemoryHeader({ snap }: { snap: ProbeSnapshot }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        width: "100%",
        fontFamily: "monospace",
        fontSize: FONT_SIZE_TINY,
      }}
    >
      <span style={{ color: "#0f0", fontWeight: "bold", marginRight: 4 }}>
        MEMORY
      </span>
      {snap.active && (
        <span
          style={{
            color: "#f44",
            fontSize: 9,
            animation: "pulse 1s ease-in-out infinite",
          }}
        >
          REC
        </span>
      )}
      <span style={{ color: COLOR_TEXT_DIM, fontSize: 9, marginLeft: "auto" }}>
        {snap.active ? fmtDuration(snap.durationMs) : "idle"}
        {" \u00b7 "}
        {snap.sampleCount} samples
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------

function Toolbar({
  snap,
  onCopy,
  onDownload,
  copied,
}: {
  snap: ProbeSnapshot;
  onCopy: () => void;
  onDownload: () => void;
  copied: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        padding: "4px 6px",
        borderBottom: "1px solid rgba(0,255,0,0.12)",
        flexShrink: 0,
      }}
    >
      <TinyButton
        active={snap.active}
        onClick={snap.active ? stopProbe : startProbe}
        color={snap.active ? "#f44" : "#0f0"}
        title={snap.active ? "Stop probe" : "Start probe"}
      >
        {snap.active ? <Square size={10} /> : <Circle size={10} />}
        <span>{snap.active ? "Stop" : "Start"}</span>
      </TinyButton>

      <TinyButton
        active={false}
        onClick={resetProbe}
        color="#aa0"
        title="Reset samples (keeps probe active)"
      >
        <RotateCcw size={10} />
      </TinyButton>

      <div style={{ flex: 1 }} />

      <TinyButton
        active={false}
        onClick={onCopy}
        color="#0f0"
        title="Copy JSON summary"
      >
        <Copy size={10} />
        <span>{copied ? "Copied" : "Copy"}</span>
      </TinyButton>

      <TinyButton
        active={false}
        onClick={onDownload}
        color="#0f0"
        title="Download full sample JSONL"
      >
        <Download size={10} />
      </TinyButton>
    </div>
  );
}

function TinyButton({
  active,
  onClick,
  color = "#0f0",
  title,
  children,
}: {
  active: boolean;
  onClick: () => void;
  color?: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Button
      data-no-drag
      onClick={onClick}
      title={title}
      variant="ghost"
      size="xs"
      style={{
        gap: 3,
        padding: "3px 7px",
        borderRadius: RADIUS_XS,
        border: "1px solid " + (active ? `${color}66` : `${color}26`),
        background: active ? `${color}26` : "transparent",
        color,
        fontFamily: "monospace",
        fontSize: 9,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

const SECTION: React.CSSProperties = {
  borderTop: "1px solid #333",
  marginTop: 6,
  paddingTop: 6,
};

const SECTION_TITLE: React.CSSProperties = {
  color: COLOR_TEXT_DIM,
  fontSize: 9,
  letterSpacing: 1,
  textTransform: "uppercase",
  marginBottom: 4,
};

function SlopesSection({ snap }: { snap: ProbeSnapshot }) {
  if (snap.sampleCount === 0) {
    return (
      <div style={{ color: COLOR_TEXT_FAINT }}>
        {snap.active ? "sampling…" : "press Start to begin sampling"}
      </div>
    );
  }
  const rows: Array<{
    label: string;
    key: string;
    unit: "bytes" | "count";
    threshold: number;
  }> = [
    { label: "heap floor", key: "heapUsed", unit: "bytes", threshold: 1024 }, // 1 KB/s ~= 60 KB/min
    { label: "geometries", key: "geometries", unit: "count", threshold: 0.01 },
    { label: "textures", key: "textures", unit: "count", threshold: 0.01 },
    { label: "materials", key: "materials", unit: "count", threshold: 0.01 },
    { label: "scene objs", key: "sceneTotal", unit: "count", threshold: 0.02 },
    { label: "meshes", key: "meshes", unit: "count", threshold: 0.02 },
    { label: "groups", key: "groups", unit: "count", threshold: 0.02 },
    { label: "lines", key: "lines", unit: "count", threshold: 0.02 },
    { label: "points", key: "points", unit: "count", threshold: 0.02 },
    { label: "instanced", key: "instanced", unit: "count", threshold: 0.02 },
    { label: "listeners", key: "listeners", unit: "count", threshold: 0.05 },
    { label: "rAF pending", key: "rafPending", unit: "count", threshold: 0.05 },
    { label: "undo bytes", key: "undoBytes", unit: "bytes", threshold: 1024 },
    { label: "redo bytes", key: "redoBytes", unit: "bytes", threshold: 1024 },
    {
      label: "undo entries",
      key: "undoEntries",
      unit: "count",
      threshold: 0.02,
    },
    {
      label: "redo entries",
      key: "redoEntries",
      unit: "count",
      threshold: 0.02,
    },
  ];
  return (
    <div style={SECTION}>
      <div style={SECTION_TITLE}>growth (trough slope)</div>
      {rows.map((r) => (
        <SlopeRow
          key={r.key}
          label={r.label}
          slope={snap.slopes[r.key]}
          unit={r.unit}
          threshold={r.threshold}
        />
      ))}
      <div style={{ ...SECTION_TITLE, marginTop: 8 }}>by userData tag</div>
      {Object.keys(snap.slopes)
        .filter((k) => k.startsWith("byTag."))
        .map((k) => (
          <SlopeRow
            key={k}
            label={k.slice("byTag.".length)}
            slope={snap.slopes[k]}
            unit="count"
            threshold={0.02}
          />
        ))}
    </div>
  );
}

function SlopeRow({
  label,
  slope,
  unit,
  threshold,
}: {
  label: string;
  slope: SlopeReport | undefined;
  unit: "bytes" | "count";
  threshold: number;
}) {
  if (!slope) return null;
  const v = verdict(slope.slopePerSec, slope.rSquared, threshold);
  const rate =
    unit === "bytes"
      ? fmtRate(slope.slopePerSec)
      : `${(slope.slopePerSec * 60).toFixed(2)}/min`;
  const cur =
    unit === "bytes" ? fmtBytes(slope.current) : String(slope.current);
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "10px 68px 1fr auto auto",
        gap: 6,
        alignItems: "center",
        lineHeight: 1.5,
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          background: v.color,
          display: "inline-block",
        }}
      />
      <span style={{ color: COLOR_TEXT_DIM }}>{label}</span>
      <span style={{ color: v.color }}>{v.label}</span>
      <span style={{ color: COLOR_TEXT_DIM, textAlign: "right" }}>{rate}</span>
      <span
        style={{ color: COLOR_TEXT_FAINT, textAlign: "right", minWidth: 50 }}
      >
        {cur}
        <span style={{ opacity: 0.5 }}> r²{slope.rSquared.toFixed(2)}</span>
      </span>
    </div>
  );
}

function OpsSection({ snap }: { snap: ProbeSnapshot }) {
  if (snap.opStats.length === 0) {
    return (
      <div style={SECTION}>
        <div style={SECTION_TITLE}>per-op floor delta</div>
        <div style={{ color: COLOR_TEXT_FAINT }}>
          no completed ops yet — trigger a gizmo drag
        </div>
      </div>
    );
  }
  return (
    <div style={SECTION}>
      <div style={SECTION_TITLE}>per-op mean deltas (per op completion)</div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "90px 28px 1fr 1fr 1fr 1fr",
          gap: 6,
          color: COLOR_TEXT_FAINT,
          lineHeight: 1.4,
          marginBottom: 2,
        }}
      >
        <span>op</span>
        <span>n</span>
        <span style={{ textAlign: "right" }}>heap</span>
        <span style={{ textAlign: "right" }}>geom</span>
        <span style={{ textAlign: "right" }}>mat</span>
        <span style={{ textAlign: "right" }}>scene</span>
      </div>
      {snap.opStats.map((row) => {
        const heapSig =
          Math.abs(row.meanFloorDelta) > 2 * row.stderrFloorDelta &&
          row.count >= 3;
        const geomLeak = row.meanGeomDelta > 0.5 && row.count >= 3;
        const matLeak = row.meanMatDelta > 0.5 && row.count >= 3;
        const sceneLeak = row.meanSceneDelta > 0.5 && row.count >= 3;
        return (
          <div
            key={row.op}
            style={{
              display: "grid",
              gridTemplateColumns: "90px 28px 1fr 1fr 1fr 1fr",
              gap: 6,
              alignItems: "center",
              lineHeight: 1.4,
            }}
          >
            <span style={{ color: COLOR_TEXT_DIM }}>{row.op}</span>
            <span style={{ color: COLOR_TEXT_FAINT, textAlign: "right" }}>
              {row.count}
            </span>
            <span
              style={{
                color: heapSig
                  ? "#f44"
                  : row.meanFloorDelta > 0
                    ? "#fa0"
                    : COLOR_TEXT_DIM,
                textAlign: "right",
              }}
            >
              {row.meanFloorDelta === 0 ? "—" : fmtBytes(row.meanFloorDelta)}
            </span>
            <span
              style={{
                color: geomLeak
                  ? "#f44"
                  : row.meanGeomDelta > 0
                    ? "#fa0"
                    : COLOR_TEXT_DIM,
                textAlign: "right",
              }}
            >
              {row.meanGeomDelta === 0 ? "0" : row.meanGeomDelta.toFixed(2)}
            </span>
            <span
              style={{
                color: matLeak
                  ? "#f44"
                  : row.meanMatDelta > 0
                    ? "#fa0"
                    : COLOR_TEXT_DIM,
                textAlign: "right",
              }}
            >
              {row.meanMatDelta === 0 ? "0" : row.meanMatDelta.toFixed(2)}
            </span>
            <span
              style={{
                color: sceneLeak
                  ? "#f44"
                  : row.meanSceneDelta > 0
                    ? "#fa0"
                    : COLOR_TEXT_DIM,
                textAlign: "right",
              }}
            >
              {row.meanSceneDelta === 0 ? "0" : row.meanSceneDelta.toFixed(2)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function PressureSection({ snap }: { snap: ProbeSnapshot }) {
  if (!snap.latest) return null;
  const latest = snap.latest;
  const rows: Array<{
    label: string;
    rate: RateReport | undefined;
    current?: string;
    color?: string;
  }> = [
    {
      label: "render calls/s",
      rate: snap.rates.renderCalls,
      current: `${fmtCount(latest.renderCalls)} total`,
      color:
        snap.rates.renderCalls.perSec > 120
          ? "#f44"
          : snap.rates.renderCalls.perSec > 60
            ? "#fa0"
            : COLOR_TEXT_DIM,
    },
    {
      label: "apply live/s",
      rate: snap.rates.applyCommandLive,
      current: `${fmtCount(latest.applyCommandLiveCalls)} total`,
      color:
        snap.rates.applyCommandLive.perSec > 120
          ? "#f44"
          : snap.rates.applyCommandLive.perSec > 60
            ? "#fa0"
            : COLOR_TEXT_DIM,
    },
    {
      label: "apply gizmo/s",
      rate: snap.rates.applyCommandLiveGizmo,
      current: `${fmtCount(latest.applyCommandLiveGizmoCalls)} total`,
      color:
        snap.rates.applyCommandLiveGizmo.perSec > 120
          ? "#f44"
          : snap.rates.applyCommandLiveGizmo.perSec > 60
            ? "#fa0"
            : COLOR_TEXT_DIM,
    },
    {
      label: "apply key/s",
      rate: snap.rates.applyCommandLiveKeyboard,
      current: `${fmtCount(latest.applyCommandLiveKeyboardCalls)} total`,
    },
    {
      label: "enter/s",
      rate: snap.rates.commandEnter,
      current: `${fmtCount(latest.commandEnterCalls)} total`,
    },
    {
      label: "confirm/s",
      rate: snap.rates.commandConfirm,
      current: `${fmtCount(latest.commandConfirmCalls)} total`,
    },
    {
      label: "cancel/s",
      rate: snap.rates.commandCancel,
      current: `${fmtCount(latest.commandCancelCalls)} total`,
    },
    {
      label: "preview patch/s",
      rate: snap.rates.liveEditPreviewPatch,
      current: `${fmtCount(latest.liveEditPreviewPatchCalls)} total`,
    },
    {
      label: "preview clear/s",
      rate: snap.rates.liveEditPreviewClear,
      current: `${fmtCount(latest.liveEditPreviewClearCalls)} total`,
    },
    {
      label: "confirm live/s",
      rate: snap.rates.commandConfirmLivePreviewCommit,
      current: `${fmtCount(latest.commandConfirmLivePreviewCommitCalls)} total`,
    },
    {
      label: "confirm sess/s",
      rate: snap.rates.commandConfirmActiveEditSessionCommit,
      current: `${fmtCount(
        latest.commandConfirmActiveEditSessionCommitCalls,
      )} total`,
    },
    {
      label: "invalidate/s",
      rate: snap.rates.invalidateCalls,
      current: `${fmtCount(latest.invalidateCalls)} total`,
      color:
        snap.rates.invalidateCalls.perSec > 120
          ? "#f44"
          : snap.rates.invalidateCalls.perSec > 60
            ? "#fa0"
            : COLOR_TEXT_DIM,
    },
    {
      label: "inv store/s",
      rate: snap.rates.invalidateStore,
      current: `${fmtCount(latest.invalidateStoreCalls)} total`,
    },
    {
      label: "inv store scene/s",
      rate: snap.rates.invalidateStoreScene,
      current: `${fmtCount(latest.invalidateStoreSceneCalls)} total`,
    },
    {
      label: "inv store edit sel/s",
      rate: snap.rates.invalidateStoreEditSelection,
      current: `${fmtCount(latest.invalidateStoreEditSelectionCalls)} total`,
    },
    {
      label: "inv store edit/s",
      rate: snap.rates.invalidateStoreEditState,
      current: `${fmtCount(latest.invalidateStoreEditStateCalls)} total`,
    },
    {
      label: "inv store cmd/s",
      rate: snap.rates.invalidateStoreCommand,
      current: `${fmtCount(latest.invalidateStoreCommandCalls)} total`,
    },
    {
      label: "inv canvas/s",
      rate: snap.rates.invalidateCanvasPointer,
      current: `${fmtCount(latest.invalidateCanvasPointerCalls)} total`,
    },
    {
      label: "inv canvas edit idle/s",
      rate: snap.rates.invalidateCanvasPointerEditIdle,
      current: `${fmtCount(latest.invalidateCanvasPointerEditIdleCalls)} total`,
    },
    {
      label: "inv canvas edit cmd/s",
      rate: snap.rates.invalidateCanvasPointerEditCommand,
      current: `${fmtCount(
        latest.invalidateCanvasPointerEditCommandCalls,
      )} total`,
    },
    {
      label: "inv direct/s",
      rate: snap.rates.invalidateDirect,
      current: `${fmtCount(latest.invalidateDirectCalls)} total`,
    },
    {
      label: "inv orbit/s",
      rate: snap.rates.invalidateOrbitControlsChange,
      current: `${fmtCount(latest.invalidateOrbitControlsChangeCalls)} total`,
    },
    {
      label: "inv cam sync/s",
      rate: snap.rates.invalidateCameraSync,
      current: `${fmtCount(latest.invalidateCameraSyncCalls)} total`,
    },
    {
      label: "inv cam move/s",
      rate: snap.rates.invalidateCameraMovement,
      current: `${fmtCount(latest.invalidateCameraMovementCalls)} total`,
    },
    {
      label: "inv transition/s",
      rate: snap.rates.invalidateCameraTransition,
      current: `${fmtCount(latest.invalidateCameraTransitionCalls)} total`,
    },
    {
      label: "inv fly/s",
      rate: snap.rates.invalidateFlyPointer,
      current: `${fmtCount(latest.invalidateFlyPointerCalls)} total`,
    },
    {
      label: "inv fly key/s",
      rate: snap.rates.invalidateFlyKey,
      current: `${fmtCount(latest.invalidateFlyKeyCalls)} total`,
    },
    {
      label: "inv key/s",
      rate: snap.rates.invalidateCameraKey,
      current: `${fmtCount(latest.invalidateCameraKeyCalls)} total`,
    },
  ];

  return (
    <div style={SECTION}>
      <div style={SECTION_TITLE}>runtime pressure</div>
      <div style={{ color: COLOR_TEXT_DIM, lineHeight: 1.4, marginBottom: 4 }}>
        schema v{snap.schemaVersion}
        {" · "}
        frame draw calls {fmtCount(latest.drawCalls)} · source{" "}
        {latest.commandSource ?? "idle"} · op {latest.op ?? "idle"}
      </div>
      {rows.map((row) => (
        <RateRow
          key={row.label}
          label={row.label}
          rate={row.rate}
          current={row.current}
          color={row.color}
        />
      ))}
    </div>
  );
}

function RateRow({
  label,
  rate,
  current,
  color = COLOR_TEXT_DIM,
}: {
  label: string;
  rate: RateReport | undefined;
  current?: string;
  color?: string;
}) {
  if (!rate) return null;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "88px 1fr auto",
        gap: 6,
        alignItems: "center",
        lineHeight: 1.4,
      }}
    >
      <span style={{ color: COLOR_TEXT_DIM }}>{label}</span>
      <span style={{ color }}>{rate.perSec.toFixed(1)}/s</span>
      <span style={{ color: COLOR_TEXT_FAINT, textAlign: "right" }}>
        {current ?? fmtCount(rate.current)}
      </span>
    </div>
  );
}

function EditPreviewSection({ snap }: { snap: ProbeSnapshot }) {
  const latest = snap.latest;
  if (!latest || (latest.editObjectVertexCount ?? 0) === 0) return null;

  return (
    <div style={SECTION}>
      <div style={SECTION_TITLE}>edit preview</div>
      <div style={{ color: COLOR_TEXT_DIM, lineHeight: 1.4 }}>
        granularity {latest.editGranularity}
        {" · "}
        mesh {fmtCount(latest.editObjectVertexCount)} verts /{" "}
        {fmtCount(latest.editObjectCornerCount)} corners /{" "}
        {fmtCount(latest.editEdgeCount)} edges
      </div>
      <div style={{ color: COLOR_TEXT_DIM, lineHeight: 1.4 }}>
        selection {fmtCount(latest.editSelectionVertexCount)} verts /{" "}
        {fmtCount(latest.editSelectionEdgeCount)} edges /{" "}
        {fmtCount(latest.editSelectionFaceCount)} faces
        {" · "}
        proportional {fmtCount(latest.proportionalVertexCount)}
      </div>
      <div style={{ color: COLOR_TEXT_DIM, lineHeight: 1.4 }}>
        overlays {fmtCount(latest.editDotCount)} dots /{" "}
        {fmtCount(latest.editHitDotCount)} hit dots /{" "}
        {fmtCount(latest.editFaceDotCount)} face dots
      </div>
      <div style={{ color: COLOR_TEXT_DIM, lineHeight: 1.4 }}>
        active session {fmtBytes(latest.activeEditSessionBytes)}
        {" · "}
        visible verts {fmtCount(latest.editVisibleVertexCount)}
      </div>
      <div style={{ color: COLOR_TEXT_DIM, lineHeight: 1.4 }}>
        lower-bound preview work {fmtBytes(latest.estPreviewMinBytes)}
      </div>
      <div style={{ color: COLOR_TEXT_FAINT, lineHeight: 1.4 }}>
        copy {fmtBytes(latest.estPreviewVertexCopyBytes)}
        {" · "}
        expanded upload {fmtBytes(latest.estPreviewExpandedPositionBytes)}
        {" · "}
        dot matrices {fmtBytes(latest.estPreviewDotMatrixBytes)}
      </div>
    </div>
  );
}

function RetainedStateSection({ snap }: { snap: ProbeSnapshot }) {
  const latest = snap.latest;
  if (!latest) return null;

  return (
    <div style={SECTION}>
      <div style={SECTION_TITLE}>retained state</div>
      <div style={{ color: COLOR_TEXT_DIM, lineHeight: 1.4 }}>
        undo {fmtCount(latest.undoEntryCount)} entries /{" "}
        {fmtBytes(latest.undoBytes)}
        {" · "}
        redo {fmtCount(latest.redoEntryCount)} / {fmtBytes(latest.redoBytes)}
      </div>
      <div style={{ color: COLOR_TEXT_DIM, lineHeight: 1.4 }}>
        undo budget {fmtBytes(latest.undoBudgetBytes)}
      </div>
      <div style={{ color: COLOR_TEXT_DIM, lineHeight: 1.4 }}>
        pre-snapshot {fmtBytes(latest.commandPreSnapshotBytes)}
        {" · "}
        cancel snapshot {fmtBytes(latest.commandCancelSnapshotBytes)}
      </div>
      <div style={{ color: COLOR_TEXT_FAINT, lineHeight: 1.4 }}>
        confirm live {fmtCount(latest.commandConfirmLivePreviewCommitCalls)}
        {" · "}
        confirm active session{" "}
        {fmtCount(latest.commandConfirmActiveEditSessionCommitCalls)}
      </div>
    </div>
  );
}

function SpikesSection({ snap }: { snap: ProbeSnapshot }) {
  if (snap.recentSpikes.length === 0) return null;
  return (
    <div style={SECTION}>
      <div style={SECTION_TITLE}>recent spikes ({snap.spikeCount} total)</div>
      {snap.recentSpikes
        .slice(-10)
        .reverse()
        .map((ev, i) => {
          const dt = snap.startedAt
            ? ((ev.t - snap.startedAt) / 1000).toFixed(1)
            : "?";
          const deltaStr =
            ev.counter === "heapUsed"
              ? fmtBytes(ev.delta)
              : ev.delta.toFixed(1);
          return (
            <div key={i} style={{ color: COLOR_TEXT_DIM, lineHeight: 1.4 }}>
              <span style={{ color: COLOR_TEXT_FAINT }}>+{dt}s</span>{" "}
              <span style={{ color: "#fa0" }}>{ev.counter}</span>{" "}
              <span style={{ color: "#f44" }}>{deltaStr}</span>{" "}
              <span style={{ color: COLOR_TEXT_FAINT }}>
                [{ev.context.mode}/{ev.context.op}]
              </span>
            </div>
          );
        })}
    </div>
  );
}
